import { Button } from "@/components/ui/button";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import React, { useEffect, useRef, useState } from "react";
import { RiSecurePaymentLine } from "react-icons/ri";
import { useToastError } from "../ui/use-toast";

export const SetupFormPaymentMethod = ({
  onCompleted,
  additionnalButtons,
  subscriptionType,
  pendingProLicenseCount,
}: {
  onCompleted: () => void;
  additionnalButtons?: React.ReactElement;
  subscriptionType?: string;
  pendingProLicenseCount?: number;
}) => {
  const api = useApi();
  const { t } = useTranslation();
  const toastError = useToastError();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const checkoutCompletedRef = useRef(false);

  useEffect(() => {
    // Listen for messages from checkout window
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === "STRIPE_CHECKOUT_SUCCESS") {
        checkoutCompletedRef.current = true;
        setIsSubmitting(false);
        onCompleted();
      } else if (event.data.type === "STRIPE_CHECKOUT_CANCELLED") {
        checkoutCompletedRef.current = true;
        setIsSubmitting(false);
        setErrorMessage(t("setup.formPaymentMethod.checkoutCancelled"));
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [onCompleted, t]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);
    checkoutCompletedRef.current = false;

    try {
      // Create Stripe Checkout Session with custom parameters
      const response = await api.post<{
        checkoutUrl: string | null;
        sessionId: string | null;
      }>("/my/team/billing/create-checkout-session", {
        subscriptionType,
        pendingProLicenseCount,
      });

      // No checkout URL means the existing subscription was updated in place
      // (a payment method is already on file) — no Stripe Checkout is needed.
      if (!response.data.checkoutUrl) {
        setIsSubmitting(false);
        onCompleted();
        return;
      }

      // Open Checkout in new tab
      const checkoutWindow = window.open(
        response.data.checkoutUrl,
        "_blank",
        "width=800,height=900,left=200,top=100"
      );

      if (!checkoutWindow) {
        setErrorMessage(t("setup.formPaymentMethod.errorPopupBlocked"));
        setIsSubmitting(false);
        return;
      }

      // Poll to detect if window is manually closed without completing payment
      const checkInterval = setInterval(() => {
        if (checkoutWindow.closed) {
          clearInterval(checkInterval);
          // If window closed and we didn't get a success message, consider it cancelled
          setTimeout(() => {
            if (!checkoutCompletedRef.current) {
              setIsSubmitting(false);
              setErrorMessage(t("setup.formPaymentMethod.checkoutCancelled"));
            }
          }, 500);
        }
      }, 500);

      // Safety timeout after 10 minutes
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!checkoutWindow.closed) {
          checkoutWindow.close();
        }
        if (!checkoutCompletedRef.current) {
          setIsSubmitting(false);
          setErrorMessage(t("setup.formPaymentMethod.checkoutCancelled"));
        }
      }, 600000);
    } catch (error) {
      console.error("Error creating checkout session:", error);
      setErrorMessage(t("setup.formPaymentMethod.errorCreatingCheckout"));
      setIsSubmitting(false);
      toastError();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("setup.formPaymentMethod.description")}
        </p>
        {errorMessage && (
          <div className="mt-2 text-red-500">{errorMessage}</div>
        )}
        <div className="space-x-2">
          {additionnalButtons}
          <Button
            type="submit"
            className="px-4 py-2 mt-4 rounded text-primary-foreground bg-primary hover:bg-primary/90"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t("setup.formPaymentMethod.loading")
              : t("setup.formPaymentMethod.validateAndContinue")}
            <RiSecurePaymentLine className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </form>
  );
};
